import { useQuery } from '@apollo/client';
import { DateTime } from 'luxon';
import { T, always, cond, equals } from 'ramda';
import { useState } from 'react';
import { useDeepCompareEffect } from 'react-use';

import useMemberInfo from '@hooks/useMemberInfo';

import { doesNotExist } from '@utils/general';

import { IAccountInfoQueryData } from '@folx-types/member';

import MEMBER_DEMOGRAPHICS_WITH_SUBS from '@graphql/queries/memberDemographicsWithSubs.query.gql';

export const useServiceEligibility = (loggedIn: boolean) => {
	const [eligible, setEligible] = useState(true);
	const { memberInfo, loading: memberLoading } = useMemberInfo();

	const { data: accountInfo, loading: accountLoading } = useQuery<IAccountInfoQueryData>(
		MEMBER_DEMOGRAPHICS_WITH_SUBS,
		{
			skip: !loggedIn,
		}
	);

	useDeepCompareEffect(() => {
		if (
			doesNotExist(accountInfo?.currentMember?.dob) ||
			doesNotExist(memberInfo?.stateOfResidence?.abbreviation) ||
			memberLoading ||
			accountLoading
		) {
			return;
		}

		const under19 =
			DateTime.fromISO(accountInfo?.currentMember?.dob ?? '')
				.diffNow('years')
				.negate().years < 19;

		const serviceEligible = cond([
			[equals('NE'), always(!under19)],
			[equals('AL'), always(!under19)],
			[T, always(true)],
		])(memberInfo?.stateOfResidence?.abbreviation ?? '');

		setEligible(serviceEligible);
	}, [memberInfo, accountInfo, memberLoading, accountLoading]);

	return {
		loading: memberLoading || accountLoading,
		eligible,
	};
};
